/**
 * Landing page (Översikt), REGTA-56.
 *
 * Leads with the "Avtalsportfölj" card — real numbers instead of
 * marketing copy (that was the point of the ticket: no false
 * advertising, and a status summary is more useful to a rental admin
 * than three entry cards):
 *
 *   - status counts: Aktiva / Utkast / Granskning / Signerade
 *   - "N frister inom 30 dagar" → links to Avtal under bearbetning
 *   - top-5 priority contracts (expiring frister, longest in review,
 *     longest ready-to-sign, reminded-but-unsigned) — category logic
 *     lives server-side in overview.portfolio
 *
 * The portfolio query is fetched with `ignoreActiveGroup` and the
 * server scopes it like the sidebar badges (org_admin → tenant-wide,
 * others → member groups) — the card must never contradict the badge
 * numbers next to it (ADR-0081).
 *
 * Palette (ADR-0081, direction picked by André 2026-07-16): the hero is
 * a full-bleed "soft sage" band — a pale tint mixed from the owner's
 * brand primary with dark-on-light text — instead of a solid primary
 * card floating on white ("the isolated square"). All tints derive from
 * the primary via color-mix, so white-label owners get their own hue.
 * The Shell drops its padding/max-width for this route; this page
 * re-applies the standard container below the band.
 *
 * The three original entry cards (guide / favoritmallar / bearbetning)
 * sit below the band, unchanged.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bell, BookOpen, Star, BellRing } from 'lucide-react';
import {
  tenantApi,
  type PortfolioContractStatus,
  type PortfolioOverview,
  type PortfolioReason,
  type PortfolioTopContract,
} from '../api.js';
import { useTenantQuery } from '../query.js';
import { useBranding, useBrandPrimary } from '../branding-context.js';
import { BRAND_ACCENT } from '../owner-branding.js';
import { usePageTitle } from '../use-page-title.js';

const HERO_ACCENT = 'var(--hero-accent)';
const HERO_BORDER = 'var(--hero-border)';
const HERO_MUTE = 'var(--hero-mute)';

const STATUS_BADGES: Record<PortfolioContractStatus, string> = {
  draft: 'Utkast',
  pending: 'Klar',
  in_review: 'Granskning',
  awaiting_manual_signature: 'Signering',
  sent: 'Skickat',
  viewed: 'Öppnat',
  awaiting_signature: 'Signering',
  signed: 'Signerad',
};

function dayCount(days: number): string {
  return days === 1 ? '1 dag' : `${days} dagar`;
}

function reasonLabel(reason: PortfolioReason, now = new Date()): string {
  if (reason.kind === 'frist') {
    const trigger = new Date(reason.triggerAt);
    const date = reason.triggerAt.slice(0, 10);
    // Deadlines materialize as midnight-UTC dates — ceil so "due
    // tomorrow" never rounds down to "idag".
    const days = Math.ceil((trigger.getTime() - now.getTime()) / 86_400_000);
    switch (reason.notificationKind) {
      case 'lease_end':
      case 'agreement_end':
        return `Förfaller ${date}`;
      case 'termination_notice':
        if (days < 0) return 'Frist passerad';
        if (days === 0) return 'Frist idag';
        return `Frist ${dayCount(days)}`;
      case 'negotiation_window':
        return `Förhandling ${date}`;
      default:
        return `Påminnelse ${date}`;
    }
  }
  if (reason.kind === 'in_review') {
    const days = Math.floor(
      (now.getTime() - new Date(reason.since).getTime()) / 86_400_000,
    );
    return days <= 0 ? 'I granskning idag' : `I granskning ${dayCount(days)}`;
  }
  if (reason.kind === 'ready_to_sign') return 'Klar för signering';
  return 'Påminnelse skickad';
}

export function OverviewPage() {
  usePageTitle('Översikt');
  const { branding } = useBranding();
  const tenantName = branding?.tenant.name?.trim() || 'din organisation';

  const q = useTenantQuery(
    'overview.portfolio',
    () => tenantApi.overview.portfolio(),
    [],
    { revalidateOnFocus: true, ignoreActiveGroup: true },
  );

  return (
    <div>
      <PortfolioCard
        tenantName={tenantName}
        data={q.data}
        loading={q.isLoading}
        failed={Boolean(q.error)}
      />

      {/* Standard container resumes below the full-bleed band. Mobile →
          single column. Tablet → 2 across with the third spanning full
          width below. Desktop → 3 across. */}
      <div className="mx-auto w-full max-w-5xl p-3 sm:p-6">
        <section className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3 pt-3 sm:pt-4">
          <HowItWorksCard />
          <FavoritesCard />
          <div className="md:col-span-2 lg:col-span-1">
            <MonitoredContractsCard />
          </div>
        </section>
      </div>
    </div>
  );
}

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p
      className="text-[10px] font-medium uppercase tracking-[0.22em]"
      style={{ color: HERO_ACCENT }}
    >
      {children}
    </p>
  );
}

function PortfolioCard({
  tenantName,
  data,
  loading,
  failed,
}: {
  tenantName: string;
  data: PortfolioOverview | undefined;
  loading: boolean;
  failed: boolean;
}) {
  const counts = data?.counts;
  const top = data?.top_contracts ?? [];
  const primary = useBrandPrimary();

  return (
    <section
      className="border-b py-8 sm:py-10"
      style={{
        // The sage is a tint of the brand ACCENT, not the primary — a
        // primary tint goes gray-green (#eaeeec) instead of the approved
        // sage (#eef3e9 ≈ 24% accent on white). Text/borders stay
        // primary-derived so a custom owner primary keeps its own hue.
        backgroundColor: `color-mix(in srgb, ${BRAND_ACCENT} 24%, white)`,
        color: `color-mix(in srgb, ${primary} 75%, black)`,
        borderColor: `color-mix(in srgb, ${primary} 16%, white)`,
        ['--hero-accent' as never]: primary,
        ['--hero-border' as never]: `color-mix(in srgb, ${primary} 20%, white)`,
        ['--hero-mute' as never]: `color-mix(in srgb, ${primary} 62%, white)`,
      }}
    >
      <div className="mx-auto w-full max-w-5xl px-3 sm:px-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Eyebrow>Avtalsportfölj</Eyebrow>
          <h1 className="font-display text-2xl sm:text-3xl font-light tracking-tight mt-2">
            {tenantName}
          </h1>
        </div>
        <Link
          to="/oversikt/avtal-under-bearbetning"
          className="inline-flex items-center gap-2 text-sm hover:underline underline-offset-4 shrink-0"
          style={{ color: HERO_MUTE }}
        >
          <Bell className="h-4 w-4" style={{ color: HERO_ACCENT }} />
          {data === undefined
            ? 'Frister inom 30 dagar'
            : data.frister_within_30 === 1
              ? '1 frist inom 30 dagar'
              : `${data.frister_within_30} frister inom 30 dagar`}
        </Link>
      </header>

      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatBox label="Aktiva" value={counts?.active} />
        <StatBox label="Utkast" value={counts?.draft} />
        <StatBox label="Granskning" value={counts?.review} />
        <StatBox label="Signerade" value={counts?.signed} />
      </div>

      <div className="mt-8">
        <div className="flex items-baseline justify-between">
          <h2>
            <Eyebrow>Avtal · Att hantera</Eyebrow>
          </h2>
          <span className="text-xs" style={{ color: HERO_MUTE }}>
            {data ? `${top.length} av ${data.counts.active}` : '–'}
          </span>
        </div>
        {failed ? (
          <p className="mt-4 text-sm" style={{ color: HERO_MUTE }}>
            Kunde inte ladda avtalsöversikten. Försök igen om en stund.
          </p>
        ) : loading || data === undefined ? (
          <p className="mt-4 text-sm" style={{ color: HERO_MUTE }}>
            Laddar…
          </p>
        ) : top.length === 0 ? (
          <p className="mt-4 text-sm" style={{ color: HERO_MUTE }}>
            Inget som behöver hanteras just nu.
          </p>
        ) : (
          <ul
            className="mt-3 divide-y border-t"
            style={{ borderColor: HERO_BORDER }}
          >
            {top.map((c) => (
              <PriorityRow key={c.contractId} contract={c} />
            ))}
          </ul>
        )}
      </div>
      </div>
    </section>
  );
}

function StatBox({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div className="border p-4" style={{ borderColor: HERO_BORDER }}>
      <Eyebrow>{label}</Eyebrow>
      <p className="font-display text-2xl sm:text-3xl font-light mt-2 tabular-nums">
        {value ?? '–'}
      </p>
    </div>
  );
}

function PriorityRow({ contract }: { contract: PortfolioTopContract }) {
  return (
    <li style={{ borderColor: HERO_BORDER }}>
      <Link
        to={`/contracts/${contract.contractId}/edit`}
        className="group flex items-center justify-between gap-4 py-3"
      >
        <span className="flex min-w-0 items-baseline gap-3">
          {contract.contractNumber && (
            <span
              className="ledger-mono text-xs shrink-0"
              style={{ color: HERO_ACCENT }}
            >
              {contract.contractNumber}
            </span>
          )}
          <span className="truncate text-sm group-hover:underline underline-offset-4">
            {contract.title}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-3">
          <span className="hidden text-xs sm:inline" style={{ color: HERO_MUTE }}>
            {reasonLabel(contract.reason)}
          </span>
          <span
            className="border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest"
            style={{ borderColor: HERO_BORDER, color: HERO_MUTE }}
          >
            {STATUS_BADGES[contract.status]}
          </span>
        </span>
      </Link>
    </li>
  );
}

function HowItWorksCard() {
  const primary = useBrandPrimary();
  return (
    <Link
      to="/oversikt/sa-fungerar-regta"
      className="group flex flex-col border border-stone-200 p-6 transition-colors hover:border-stone-400"
    >
      <div className="flex items-start justify-between">
        <div
          className="flex h-10 w-10 items-center justify-center text-white"
          style={{ backgroundColor: primary }}
        >
          <BookOpen className="h-4 w-4" />
        </div>
        <span className="text-[10px] font-medium uppercase tracking-widest text-stone-400">
          Guide
        </span>
      </div>
      <h3 className="font-display text-xl font-light pt-6 text-stone-900">
        Så fungerar Regta
      </h3>
      <p className="text-sm text-stone-500 leading-relaxed mt-2 flex-1">
        Snabb introduktion till mallar, avtalsskapande, signering och
        bevakning — så du och teamet kommer igång rätt.
      </p>
      <span
        className="inline-flex items-center gap-1 text-sm mt-6"
        style={{ color: primary }}
      >
        Öppna guiden
        <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}

function FavoritesCard() {
  const primary = useBrandPrimary();
  return (
    <Link
      to="/templates"
      className="group flex flex-col border border-stone-200 p-6 transition-colors hover:border-stone-400"
    >
      <div className="flex items-start justify-between">
        <div
          className="flex h-10 w-10 items-center justify-center border-2 bg-white"
          style={{ borderColor: primary, color: primary }}
        >
          <Star className="h-4 w-4" />
        </div>
        <span className="text-[10px] font-medium uppercase tracking-widest text-stone-400">
          Mallar
        </span>
      </div>
      <h3 className="font-display text-xl font-light pt-6 text-stone-900">
        Användarens favoritmallar
      </h3>
      <p className="text-sm text-stone-500 leading-relaxed mt-2 flex-1">
        Genvägar till de mallar du använder oftast — direkt till
        mallbiblioteket där du kan skapa ett nytt avtal.
      </p>
      <span
        className="inline-flex items-center gap-1 text-sm mt-6"
        style={{ color: primary }}
      >
        Öppna mallbiblioteket
        <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}

function MonitoredContractsCard() {
  const primary = useBrandPrimary();
  return (
    <Link
      to="/oversikt/avtal-under-bearbetning"
      className="group flex flex-col border border-stone-200 p-6 transition-colors hover:border-stone-400"
    >
      <div className="flex items-start justify-between">
        <div
          className="flex h-10 w-10 items-center justify-center text-white"
          style={{ backgroundColor: primary }}
        >
          <BellRing className="h-4 w-4" />
        </div>
        <span className="text-[10px] font-medium uppercase tracking-widest text-stone-400">
          Påminnelser
        </span>
      </div>
      <h3 className="font-display text-xl font-light pt-6 text-stone-900">
        Avtal under bearbetning
      </h3>
      <p className="text-sm text-stone-500 leading-relaxed mt-2 flex-1">
        Avtal grupperade efter nästa påminnelse — förhandlingsordning,
        uppsägning, lokalavtal. Sorterat på närmast i tid.
      </p>
      <span
        className="inline-flex items-center gap-1 text-sm mt-6"
        style={{ color: primary }}
      >
        Öppna listan
        <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}
